import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { QuestionDialogComponent } from '../components/question-dialog/question-dialog.component';
import { Question } from './models/question.model';
import { SharedService } from './shared.service';
import { ViewMode } from 'bitmovin-player';

@Injectable({
  providedIn: 'root',
})
export class QuestionService {
  private questions: Question[] = [];
  private currentTimeSubject = new BehaviorSubject<number>(0);
  private shouldPauseVideoSubject = new BehaviorSubject<boolean>(false);
  private seekToTimeSubject = new Subject<number>();
  private shouldSwitchToInlineSubject = new BehaviorSubject<boolean>(false);

  // Observables
  currentTime$ = this.currentTimeSubject.asObservable();
  shouldPauseVideo$ = this.shouldPauseVideoSubject.asObservable();
  seekToTime$ = this.seekToTimeSubject.asObservable();
  shouldSwitchToInline$ = this.shouldSwitchToInlineSubject.asObservable();

  private activeDialog = false;
  private questionsShown = new Set<number>();

  private playerViewMode = ViewMode.Inline;
  playerViewMode$ = new BehaviorSubject<ViewMode>(this.playerViewMode);

  constructor(
    private sharedService: SharedService,
    private dialog: MatDialog
  ) {}

  get getPlayerViewMode(): ViewMode {
    return this.playerViewMode;
  }

  setPlayerViewMode(viewMode: ViewMode): void {
    this.playerViewMode = viewMode;
    this.playerViewMode$.next(this.playerViewMode);
  }

  /**
   * Initialize questions data
   */
  setQuestions(questions: Question[]): void {
    this.questions = [...questions];
    this.questionsShown.clear();
  }

  /**
   * Update current video time and check for questions
   */
  updateCurrentTime(time: number): void {
    this.currentTimeSubject.next(time);
    this.checkForQuestions(time);
  }

  /**
   * Check if any questions should be shown at the current time
   */
  private checkForQuestions(currentTime: number): void {
    if (this.activeDialog) {
      return;
    }

    const questionsToShow = this.questions.filter(
      (q) => q.timeCode <= currentTime && !this.questionsShown.has(q.id)
    );
    // Sort questions by timeCode in descending order
    questionsToShow.sort((a, b) => b.timeCode - a.timeCode);

    if (questionsToShow.length > 0) {
      const nextQuestion = questionsToShow.reduce((prev, current) =>
        prev.timeCode > current.timeCode ? prev : current
      );

      // If the difference is less than 0.5 seconds, show the question
      if (Math.abs(nextQuestion.timeCode - currentTime) < 0.5) {
        this.showQuestion(nextQuestion);
      }
    }
  }

  /**
   * Display question dialog and handle the result
   */
  showQuestion(question: Question): void {
    this.shouldPauseVideoSubject.next(true);
    this.activeDialog = true;
    this.questionsShown.add(question.id);

    this.sharedService.setModelOpen(true);

    this.shouldSwitchToInlineSubject.next(true);

    const config: MatDialogConfig = {
      width: '600px',
      disableClose: true,
      data: { question },
    };

    const dialogRef = this.dialog.open(QuestionDialogComponent, config);

    dialogRef.afterClosed().subscribe((result) => {
      this.activeDialog = false;

      if (result && result.nextQuestionId) {
        const nextQuestion = this.questions.find(
          (q) => q.id === result.nextQuestionId
        );
        if (nextQuestion) {
          this.showQuestion(nextQuestion);
          return;
        }
      }

      if (result && !result.nextQuestionId) {
        this.seekToTimeSubject.next(result.nextTimeCode);
      } else {
        this.shouldPauseVideoSubject.next(false);
      }
    });
  }

  /**
   * Show a specific question by ID
   */
  showQuestionById(questionId: number): void {
    const question = this.questions.find((q) => q.id === questionId);
    if (question) {
      this.showQuestion(question);
    }
  }

  /**
   * Reset all shown questions (for replay)
   */
  resetQuestions(): void {
    this.questionsShown.clear();
  }
}
